import { Component, inject } from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { TeamLeaderEffects } from "../../State/TeamLeaderEffect";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { PartnerApi } from "../../../Partner/Api/PartnerApi";
import { PartnerSearchRequest } from "../../../Partner/Dto/PartnerSearchRequest";
import { CreateTeamLeaderRequest } from "../../Dto/TeamLeaderCreateRequest";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-team-leader-create",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, AutocompleteComponent, LocationSelectorComponent],
  templateUrl: "./TeamLeaderCreatePage.html",
  styleUrl: "./TeamLeaderCreatePage.scss",
})
export class TeamLeaderCreatePage {
  private readonly router = inject(Router);
  private readonly teamLeaderEffects = inject(TeamLeaderEffects);
  private readonly partnerApi = inject(PartnerApi);
  private readonly toastEffects = inject(ToastEffects);
  private readonly fb = inject(FormBuilder);

  teamLeaderForm: FormGroup;
  isSaving = false;
  selectedPartnerIds: string[] = [];
  searchedPartners: AutocompleteOption[] = [];

  constructor() {
    this.teamLeaderForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      email: [""],
      phone: [""],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        longitude: [0],
        latitude: [0],
      }),
    });
  }

  async searchPartners(term: string): Promise<void> {
    const params: PartnerSearchRequest = {
      search: term,
      page: 1,
      limit: 50,
    };

    const response = await this.partnerApi.searchPartners(params);
    const searchResults = response.data.map((inst) => ({
      value: inst.id,
      label: inst.name,
    }));

    const existingSelectedPartners = this.searchedPartners.filter((existing) =>
      this.selectedPartnerIds.includes(existing.value.toString()),
    );

    const existingIds = new Set(searchResults.map((inst) => inst.value.toString()));
    this.searchedPartners = [
      ...searchResults,
      ...existingSelectedPartners.filter((inst) => !existingIds.has(inst.value.toString())),
    ];
  }

  onPartnerChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      this.selectedPartnerIds = value.map((v) => v.toString());
    }
  }

  async saveTeamLeader(): Promise<void> {
    if (this.teamLeaderForm.invalid) {
      this.teamLeaderForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    this.isSaving = true;

    const formData = this.teamLeaderForm.value;
    const payload: CreateTeamLeaderRequest = {
      name: formData.name,
      email: formData.email || null,
      phone: formData.phone || null,
      address: {
        stateProvinceRegion: formData.address.stateProvinceRegion,
        countyDistrict: formData.address.countyDistrict,
        subdistrictMunicipality: formData.address.subdistrictMunicipality,
        communeVillage: formData.address.communeVillage,
        line: formData.address.line || "",
        zip: formData.address.zip || "",
        longitude: 0,
        latitude: 0,
      },
      partners: this.selectedPartnerIds,
    };

    try {
      await this.teamLeaderEffects.createTeamLeader(payload);

      this.toastEffects.show("Team leader created successfully.", "success");
      await this.router.navigate(["/team-leaders"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create team leader."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/team-leaders"]).catch(() => undefined);
  }

  onAddressChange(value: LocationAddress): void {
    this.teamLeaderForm.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
      },
    });
  }
}
